import { formatMoney } from '@theme/money-formatting';
import { readList, writeList, clearList, addItem, toQuantity } from '@theme/quote-list';

/** @typedef {import('@theme/quote-list').QuoteItem} QuoteItem */
/** @typedef {import('@theme/quote-list').QuoteList} QuoteList */

/**
 * @param {string} tag
 * @param {string} [className]
 * @param {string} [text]
 */
function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

/**
 * The quote request page: the product list, the "not in our store" list and the
 * contact form that sends both.
 */
class QuoteRequest extends HTMLElement {
  /** @type {any} */
  #config;
  /** @type {QuoteList} */
  #list = { items: [], custom: [] };
  /** @type {Map<string, any>} Product JSON by handle */
  #products = new Map();
  /** @type {AbortController | null} */
  #searchAbort = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #searchTimer;

  async connectedCallback() {
    const config = this.querySelector('script[data-config]');
    if (!config?.textContent) return;
    this.#config = JSON.parse(config.textContent);

    // The form posted and Shopify sent it: start the next request empty.
    if (this.querySelector('[data-posted]')) {
      clearList();
      return;
    }

    this.#list = readList();
    this.#field('search')?.addEventListener('input', this.#onSearchInput);
    this.#field('search')?.addEventListener('keydown', this.#onSearchKey);
    this.#field('add-custom')?.addEventListener('click', this.#addCustom);
    this.querySelector('[name="contact[body]"]')?.addEventListener('input', this.#updateValidity);
    document.addEventListener('click', this.#onDocumentClick);

    const params = new URLSearchParams(location.search);
    if (params.get('from') === 'cart') {
      await this.#importCart();
      params.delete('from');
      const query = params.toString();
      history.replaceState(history.state, '', `${location.pathname}${query ? `?${query}` : ''}${location.hash}`);
    }

    if (this.#list.custom.length === 0) this.#list.custom.push({ product: '', quantity: 1, note: '' });
    this.#renderCustom();
    this.#updateValidity();
    await this.#loadProducts();
    this.#renderItems();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocumentClick);
  }

  /** @param {string} name */
  #field(name) {
    return /** @type {HTMLElement | null} */ (this.querySelector(`[data-${name}]`));
  }

  /** @param {number} amount */
  #money(amount) {
    return formatMoney(amount, this.#config.moneyFormat, this.#config.currency);
  }

  /** @param {string} path */
  #url(path) {
    const root = this.#config.root === '/' ? '' : this.#config.root;
    return `${root}${path}`;
  }

  /**
   * Stores the list and keeps the hidden "Products" field current. Shopify's spam
   * check takes over the form's submit and posts it itself, so the field is filled on
   * every change rather than in a submit handler.
   */
  #save() {
    writeList({
      items: this.#list.items,
      custom: this.#list.custom.filter((line) => line.product.trim() || line.note.trim()),
    });

    const summary = /** @type {HTMLTextAreaElement | null} */ (this.#field('summary'));
    if (summary) summary.value = this.#summary();
    this.#updateValidity();
  }

  /**
   * An empty request is blocked by the browser's own form validation, which runs
   * before the submit event (and so before Shopify's spam check posts the form).
   */
  #updateValidity = () => {
    const search = /** @type {HTMLInputElement | null} */ (this.#field('search'));
    const message = /** @type {HTMLTextAreaElement | null} */ (this.querySelector('[name="contact[body]"]'));
    if (!search) return;

    const hasContent =
      this.#list.items.length > 0 ||
      this.#list.custom.some((line) => line.product.trim()) ||
      Boolean(message?.value.trim());
    search.setCustomValidity(hasContent ? '' : this.#config.strings.nothingToSend);
  };

  async #importCart() {
    try {
      const cart = await fetch(`${this.#config.cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );
      // The cart's quantities win, so following the cart link twice doesn't double them.
      for (const line of cart.items ?? []) {
        const existing = this.#list.items.find((item) => item.variantId === line.variant_id);
        if (existing) {
          existing.quantity = line.quantity;
        } else {
          this.#list.items.push({ handle: line.handle, variantId: line.variant_id, quantity: line.quantity });
        }
      }
      this.#save();
    } catch {
      this.#setStatus(this.#config.strings.error);
    }
  }

  async #loadProducts() {
    const handles = [...new Set(this.#list.items.map((item) => item.handle))].filter(
      (handle) => !this.#products.has(handle)
    );

    await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(this.#url(`/products/${encodeURIComponent(handle)}.js`));
          if (response.ok) this.#products.set(handle, await response.json());
        } catch {
          // Shown as unavailable below.
        }
      })
    );

    // Drop products that no longer exist (deleted or unpublished since they were added).
    this.#list.items = this.#list.items.filter((item) => this.#products.has(item.handle));
    this.#save();
  }

  /** @param {QuoteItem} item */
  #variant(item) {
    const product = this.#products.get(item.handle);
    return product?.variants?.find((/** @type {any} */ variant) => variant.id === item.variantId) ?? null;
  }

  #renderItems() {
    const list = this.#field('lines');
    const empty = this.#field('empty');
    const totalRow = this.#field('total-row');
    const total = this.#field('total');
    if (!list || !empty || !totalRow || !total) return;

    const strings = this.#config.strings;
    let sum = 0;

    const rows = this.#list.items.map((item, index) => {
      const product = this.#products.get(item.handle);
      const variant = this.#variant(item);
      const row = element('li', 'quote-request__line');
      const media = element('div', 'quote-request__line-media');
      const details = element('div', 'quote-request__line-details');
      const title = /** @type {HTMLAnchorElement} */ (element('a', 'quote-request__line-title', product.title));
      const controls = element('div', 'quote-request__line-controls');

      const imageUrl = variant?.featured_image?.src ?? product.featured_image;
      if (imageUrl) {
        const image = /** @type {HTMLImageElement} */ (element('img'));
        const src = new URL(imageUrl, location.origin);
        src.searchParams.set('width', '160');
        image.src = src.toString();
        image.alt = '';
        image.loading = 'lazy';
        image.width = 80;
        image.height = 80;
        media.append(image);
      }

      title.href = this.#url(`/products/${item.handle}${variant ? `?variant=${variant.id}` : ''}`);
      details.append(title);

      if (product.variants.length > 1) {
        const select = /** @type {HTMLSelectElement} */ (element('select', 'quote-request__input quote-request__select'));
        select.setAttribute('aria-label', strings.variant);
        for (const option of product.variants) {
          const node = /** @type {HTMLOptionElement} */ (element('option', '', option.title));
          node.value = String(option.id);
          node.selected = option.id === item.variantId;
          select.append(node);
        }
        select.addEventListener('change', () => {
          item.variantId = Number(select.value);
          this.#save();
          this.#renderItems();
        });
        details.append(select);
      }

      if (variant) {
        details.append(element('span', 'quote-request__hint', `${strings.unitPrice}: ${this.#money(variant.price)}`));
        if (!variant.available) details.append(element('span', 'quote-request__hint', strings.soldOut));
        sum += variant.price * item.quantity;
      }

      const quantity = /** @type {HTMLInputElement} */ (element('input', 'quote-request__input quote-request__quantity'));
      quantity.type = 'number';
      quantity.min = '1';
      quantity.max = '9999';
      quantity.inputMode = 'numeric';
      quantity.value = String(item.quantity);
      quantity.setAttribute('aria-label', `${strings.quantity}: ${product.title}`);
      quantity.addEventListener('change', () => {
        item.quantity = toQuantity(quantity.value);
        quantity.value = String(item.quantity);
        this.#save();
        this.#renderTotal();
      });

      const remove = /** @type {HTMLButtonElement} */ (element('button', 'quote-request__remove button-unstyled', '×'));
      remove.type = 'button';
      remove.setAttribute('aria-label', `${strings.remove}: ${product.title}`);
      remove.addEventListener('click', () => {
        this.#list.items.splice(index, 1);
        this.#save();
        this.#renderItems();
        /** @type {HTMLElement | null} */ (this.#field('search'))?.focus();
      });

      controls.append(quantity, remove);
      row.append(media, details, controls);
      return row;
    });

    list.replaceChildren(...rows);
    empty.hidden = rows.length > 0;
    totalRow.hidden = rows.length === 0;
    total.textContent = this.#money(sum);
  }

  #renderTotal() {
    const total = this.#field('total');
    if (!total) return;
    const sum = this.#list.items.reduce((amount, item) => amount + (this.#variant(item)?.price ?? 0) * item.quantity, 0);
    total.textContent = this.#money(sum);
  }

  #renderCustom() {
    const list = this.#field('custom');
    if (!list) return;
    const strings = this.#config.strings;

    const rows = this.#list.custom.map((line, index) => {
      const row = element('li', 'quote-request__custom-line');

      const product = /** @type {HTMLInputElement} */ (element('input', 'quote-request__input'));
      product.type = 'text';
      product.placeholder = strings.customProduct;
      product.setAttribute('aria-label', strings.customProduct);
      product.value = line.product;
      product.maxLength = 200;
      product.addEventListener('input', () => {
        line.product = product.value;
        this.#save();
      });

      const quantity = /** @type {HTMLInputElement} */ (element('input', 'quote-request__input quote-request__quantity'));
      quantity.type = 'number';
      quantity.min = '1';
      quantity.max = '9999';
      quantity.inputMode = 'numeric';
      quantity.value = String(line.quantity);
      quantity.setAttribute('aria-label', strings.quantity);
      quantity.addEventListener('change', () => {
        line.quantity = toQuantity(quantity.value);
        quantity.value = String(line.quantity);
        this.#save();
      });

      const note = /** @type {HTMLInputElement} */ (element('input', 'quote-request__input quote-request__custom-note'));
      note.type = 'text';
      note.placeholder = strings.customNote;
      note.setAttribute('aria-label', strings.customNote);
      note.value = line.note;
      note.maxLength = 300;
      note.addEventListener('input', () => {
        line.note = note.value;
        this.#save();
      });

      const remove = /** @type {HTMLButtonElement} */ (element('button', 'quote-request__remove button-unstyled', '×'));
      remove.type = 'button';
      remove.setAttribute('aria-label', strings.remove);
      remove.addEventListener('click', () => {
        this.#list.custom.splice(index, 1);
        if (this.#list.custom.length === 0) this.#list.custom.push({ product: '', quantity: 1, note: '' });
        this.#save();
        this.#renderCustom();
      });

      row.append(product, quantity, remove, note);
      return row;
    });

    list.replaceChildren(...rows);
  }

  #addCustom = () => {
    this.#list.custom.push({ product: '', quantity: 1, note: '' });
    this.#renderCustom();
    const inputs = this.#field('custom')?.querySelectorAll('li:last-child input');
    /** @type {HTMLInputElement | undefined} */ (inputs?.[0])?.focus();
  };

  /** @param {Event} event */
  #onSearchInput = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    clearTimeout(this.#searchTimer);
    const term = input.value.trim();
    if (term.length < 2) {
      this.#closeResults();
      return;
    }
    this.#searchTimer = setTimeout(() => this.#search(term), 200);
  };

  /** @param {KeyboardEvent} event */
  #onSearchKey = (event) => {
    const results = this.#field('results');
    if (!results || results.hidden) return;
    const options = /** @type {HTMLElement[]} */ ([...results.querySelectorAll('[role="option"]')]);
    if (options.length === 0) return;
    const current = options.findIndex((option) => option.getAttribute('aria-selected') === 'true');

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const next =
        event.key === 'ArrowDown'
          ? (current + 1) % options.length
          : (current - 1 + options.length) % options.length;
      this.#highlight(options, next);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      options[Math.max(current, 0)]?.click();
    } else if (event.key === 'Escape') {
      this.#closeResults();
    }
  };

  /**
   * @param {HTMLElement[]} options
   * @param {number} index
   */
  #highlight(options, index) {
    const search = this.#field('search');
    options.forEach((option, position) => option.setAttribute('aria-selected', String(position === index)));
    search?.setAttribute('aria-activedescendant', options[index]?.id ?? '');
    options[index]?.scrollIntoView({ block: 'nearest' });
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!this.querySelector('.quote-request__search')?.contains(/** @type {Node} */ (event.target))) {
      this.#closeResults();
    }
  };

  #closeResults() {
    const results = this.#field('results');
    const search = this.#field('search');
    if (results) results.hidden = true;
    search?.setAttribute('aria-expanded', 'false');
    search?.removeAttribute('aria-activedescendant');
  }

  /** @param {string} term */
  async #search(term) {
    const results = this.#field('results');
    const search = this.#field('search');
    if (!results || !search) return;

    this.#searchAbort?.abort();
    this.#searchAbort = new AbortController();

    const url = new URL(`${this.#config.searchUrl}.json`, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '8');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,vendor');

    let products = [];
    try {
      const response = await fetch(url, { signal: this.#searchAbort.signal });
      const data = await response.json();
      products = data?.resources?.results?.products ?? [];
    } catch (error) {
      if (/** @type {Error} */ (error).name === 'AbortError') return;
    }

    const strings = this.#config.strings;
    const options = products.map((/** @type {any} */ product, /** @type {number} */ index) => {
      const option = element('li', 'quote-request__result');
      option.id = `${results.id}-${index}`;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');

      if (product.image) {
        const image = /** @type {HTMLImageElement} */ (element('img'));
        const src = new URL(product.image, location.origin);
        src.searchParams.set('width', '96');
        image.src = src.toString();
        image.alt = '';
        image.width = 40;
        image.height = 40;
        option.append(image);
      }

      option.append(element('span', 'quote-request__result-title', product.title));
      option.addEventListener('click', () => this.#pick(product.handle, product.title));
      return option;
    });

    if (options.length === 0) {
      options.push(element('li', 'quote-request__result quote-request__result--empty', strings.noResults.replace('__TERMS__', term)));
    }

    results.replaceChildren(...options);
    results.hidden = false;
    search.setAttribute('aria-expanded', 'true');
  }

  /**
   * @param {string} handle
   * @param {string} title
   */
  async #pick(handle, title) {
    const search = /** @type {HTMLInputElement | null} */ (this.#field('search'));
    this.#closeResults();

    try {
      if (!this.#products.has(handle)) {
        const response = await fetch(this.#url(`/products/${encodeURIComponent(handle)}.js`));
        if (!response.ok) throw new Error(String(response.status));
        this.#products.set(handle, await response.json());
      }
      const product = this.#products.get(handle);
      const variant = product.variants.find((/** @type {any} */ v) => v.available) ?? product.variants[0];
      addItem(this.#list, { handle, variantId: variant.id, quantity: 1 });
      this.#save();
      this.#renderItems();
      this.#setStatus(this.#config.strings.added.replace('__TITLE__', title), 'polite');
      if (search) {
        search.value = '';
        search.focus();
      }
    } catch {
      this.#setStatus(this.#config.strings.error);
    }
  }

  /**
   * @param {string} message
   * @param {'polite' | 'alert'} [tone]
   */
  #setStatus(message, tone = 'alert') {
    const status = this.#field('status');
    if (!status) return;
    status.setAttribute('role', tone === 'alert' ? 'alert' : 'status');
    status.textContent = message;
  }

  /** Plain-text list for the store's contact email. */
  #summary() {
    const lines = [];
    const origin = location.origin;

    if (this.#list.items.length) {
      lines.push('STORE PRODUCTS');
      for (const item of this.#list.items) {
        const product = this.#products.get(item.handle);
        if (!product) continue;
        const variant = this.#variant(item);
        const name = variant && product.variants.length > 1 ? `${product.title} (${variant.title})` : product.title;
        const sku = variant?.sku ? `, SKU ${variant.sku}` : '';
        const price = variant ? `, listed ${this.#money(variant.price)} each` : '';
        const stock = variant && !variant.available ? ', out of stock' : '';
        lines.push(`${item.quantity} x ${name}${sku}${price}${stock}`);
        lines.push(`   ${origin}/products/${item.handle}?variant=${item.variantId}`);
      }
    }

    const custom = this.#list.custom.filter((line) => line.product.trim());
    if (custom.length) {
      if (lines.length) lines.push('');
      lines.push('NOT IN STORE');
      for (const line of custom) {
        const note = line.note.trim() ? ` (${line.note.trim()})` : '';
        lines.push(`${line.quantity} x ${line.product.trim()}${note}`);
      }
    }

    return lines.join('\n');
  }
}

if (!customElements.get('quote-request')) customElements.define('quote-request', QuoteRequest);
